import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import { useLocale } from '../../../../context/LocaleContext';
import { useOnClickOutside } from '../../../lib/hooks/use-on-click-outside';

import { languages } from '../config';

import styles from './select.module.css';

export function Select() {
	const [isOpen, setIsOpen] = useState(false);
	const ref = useRef(null);

	const { locale } = useIntl();
	const { handleSetLocale } = useLocale();

	const selected =
		languages.find(item => item.value === locale) ?? languages[0];

	const handleClose = useCallback(() => setIsOpen(false), []);
	useOnClickOutside(ref, handleClose);

	const handleChangeLocale = next => {
		handleSetLocale(next);
		handleClose();
	};

	return (
		<div
			ref={ref}
			className={styles.select}
			onKeyDown={event => event.key === 'Escape' && handleClose()}
		>
			<button
				type='button'
				className={styles.selectTrigger}
				id='public-lang-menu-button'
				aria-expanded={isOpen}
				aria-haspopup='true'
				onClick={() => setIsOpen(open => !open)}
			>
				<selected.flag className={styles.selectFlag} aria-hidden />
				<span className={styles.selectValue}>{selected.label}</span>
				<ChevronDown
					size={20}
					strokeWidth={2}
					aria-hidden
					className={clsx(
						styles.selectChevron,
						isOpen && styles.selectChevronOpen,
					)}
				/>
			</button>

			{isOpen && (
				<div
					className={styles.selectMenu}
					role='menu'
					aria-orientation='vertical'
					aria-labelledby='public-lang-menu-button'
				>
					{languages.map(item => {
						const isActive = item.value === locale;

						return (
							<button
								key={item.id}
								type='button'
								role='menuitemradio'
								aria-checked={isActive}
								title={item.label}
								className={clsx(
									styles.selectOption,
									isActive && styles.selectOptionActive,
								)}
								onClick={() => handleChangeLocale(item.value)}
							>
								<item.flag />
								<span>{item.label}</span>
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
}
